import { OrbindApiError } from '@orbind/api-client'
import { Button, Checkbox, FieldBlock, LoadingState, Modal, Select, useToast } from '@orbind/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { FormScreen, FormSection } from '../../components/FormScreen.tsx'
import { IconField } from '../../components/IconPicker.tsx'
import { useConfirm } from '../work-item/bits.tsx'
import { api } from '../../lib/api.ts'
import { useBootstrap } from '../../lib/catalog.ts'
import { useT } from '../../lib/i18n.tsx'
import './flows.css'

/**
 * DESIGNER de processo (SPEC-20260815-1204): a area de desenho do fluxo.
 *
 * Implementado do zero (sem react-flow): nos sao divs posicionadas por
 * pointer events e as ligacoes sao caminhos SVG com seta. Adicionar etapa
 * oferece as ETAPAS EXISTENTES do catalogo (uma etapa entra uma unica vez por
 * fluxo) ou cria uma nova inline. Salvar manda o grafo inteiro numa transacao
 * — o servidor preserva os ids dos nos mantidos (lanes apontam para eles).
 */

type No = {
  processStepId: string
  name: string
  color: string | null
  /** Icone da etapa (2026-08-30) — vale no cadastro, nao so neste fluxo. */
  icon?: string | null
  x: number
  y: number
}
type Lado = 'top' | 'right' | 'bottom' | 'left'
/**
 * Curvatura escolhida pela pessoa (SPEC-20261103-2312): deslocamento, em
 * unidades do canvas, aplicado aos dois pontos de controle da curva, medido a
 * partir do meio da reta entre as ancoras — por isso sobrevive a mover as
 * etapas. Nulo = curva automatica.
 */
type Curvatura = { x: number; y: number }
type Aresta = { from: string; to: string; fromSide: Lado | null; toSide: Lado | null; bend: Curvatura | null }

const LARGURA_NO = 200
// altura REAL do cartao (travada no CSS .flownode) — as setas ancoram no
// CENTRO das faces a partir dela (pedido 2026-08-25)
const ALTURA_NO = 42

/**
 * Canvas "infinito na pratica" (pedido do usuario 2026-08-15): comeca grande,
 * ABRE CENTRALIZADO (da para mover em todas as direcoes) e cresce quando o
 * desenho ou o pan atingem as bordas.
 */
const TAMANHO_MIN = { w: 6000, h: 4000 }
const PASSO_EXPANSAO = 600
const TAMANHO_MAX = 40000
const MARGEM_BORDA = 80

/**
 * Largura do cartao: ALARGA para caber nomes compridos numa linha so (pedido
 * do usuario 2026-08-15 — nada de quebrar o nome). Estimativa por tamanho do
 * texto (fonte 13px ~7.6px/char) + espacos fixos (padding, lixeira).
 */
const larguraDe = (n: No) => Math.min(520, Math.max(LARGURA_NO, Math.ceil(68 + n.name.length * 7.6)))

/** Ponto central de um lado do cartao. */
function ancora(n: No, lado: Lado) {
  switch (lado) {
    case 'top':
      return { x: n.x + larguraDe(n) / 2, y: n.y }
    case 'bottom':
      return { x: n.x + larguraDe(n) / 2, y: n.y + ALTURA_NO }
    case 'left':
      return { x: n.x, y: n.y + ALTURA_NO / 2 }
    case 'right':
      return { x: n.x + larguraDe(n), y: n.y + ALTURA_NO / 2 }
  }
}

const DIRECAO: Record<Lado, { x: number; y: number }> = {
  top: { x: 0, y: -1 },
  bottom: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
}

/** Lados automaticos (eixo dominante entre os centros) quando nao escolhidos. */
function ladosAuto(a: No, b: No): [Lado, Lado] {
  const dx = b.x + larguraDe(b) / 2 - (a.x + larguraDe(a) / 2)
  const dy = b.y + ALTURA_NO / 2 - (a.y + ALTURA_NO / 2)
  if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? ['right', 'left'] : ['left', 'right']
  return dy >= 0 ? ['bottom', 'top'] : ['top', 'bottom']
}

/** Lado do cartao mais proximo de um ponto — para soltar a seta onde quiser. */
function ladoMaisProximo(n: No, p: { x: number; y: number }): Lado {
  const lados: Lado[] = ['top', 'right', 'bottom', 'left']
  let melhor: Lado = 'left'
  let menor = Number.POSITIVE_INFINITY
  for (const lado of lados) {
    const a = ancora(n, lado)
    const d = (a.x - p.x) ** 2 + (a.y - p.y) ** 2
    if (d < menor) {
      menor = d
      melhor = lado
    }
  }
  return melhor
}

/** Raio (em unidades do canvas) em que soltar "pega" a PORTA exata. */
const RAIO_DA_PORTA = 18
/** Folga em volta do cartao em que soltar ainda conta como "no cartao". */
const FOLGA_DO_CARTAO = 12

/**
 * Onde a seta vai ENTRAR se soltar aqui (SPEC-20261103-2304). Resolvido por
 * GEOMETRIA, nao pelo elemento sob o ponteiro: a camada das alcas, a porta
 * de outra ligacao ou o proprio SVG ficavam entre o ponteiro e o cartao e o
 * soltar caia no vazio (criava etapa nova) ou no lado errado. Prioridade:
 * a PORTA mais perto dentro do raio (o ponto que a pessoa mirou) e, se nao,
 * o cartao sob o ponteiro com o lado mais proximo.
 */
function alvoDaLigacao(nos: No[], p: { x: number; y: number }, excluir: string | null): { id: string; lado: Lado; exato: boolean } | null {
  let porta: { id: string; lado: Lado; d: number } | null = null
  for (const n of nos) {
    if (n.processStepId === excluir) continue
    for (const lado of ['top', 'right', 'bottom', 'left'] as const) {
      const a = ancora(n, lado)
      const d = Math.hypot(a.x - p.x, a.y - p.y)
      if (d <= RAIO_DA_PORTA && (!porta || d < porta.d)) porta = { id: n.processStepId, lado, d }
    }
  }
  if (porta) return { id: porta.id, lado: porta.lado, exato: true }
  for (const n of nos) {
    if (n.processStepId === excluir) continue
    const dentro =
      p.x >= n.x - FOLGA_DO_CARTAO &&
      p.x <= n.x + larguraDe(n) + FOLGA_DO_CARTAO &&
      p.y >= n.y - FOLGA_DO_CARTAO &&
      p.y <= n.y + ALTURA_NO + FOLGA_DO_CARTAO
    if (dentro) return { id: n.processStepId, lado: ladoMaisProximo(n, p), exato: false }
  }
  return null
}

/**
 * Curva de A para B com seta. Cada ponta usa o lado ESCOLHIDO no desenho
 * (arrastando a bolinha/a ponta da seta) ou, sem escolha, o lado automatico
 * pelo eixo dominante entre os centros.
 */
/**
 * `curvada` = a pessoa escolheu a curvatura: as tangentes encurtam (um quarto
 * da distancia em vez de metade) para os dois pontos de controle nao
 * coincidirem — com eles juntos, deslocar os dois faz um bico, nao um arco.
 * Sem curvatura escolhida nada muda em relacao ao desenho de sempre.
 */
function geometria(a: No, b: No, fromSide: Lado | null, toSide: Lado | null, curvada = false) {
  const [autoDe, autoPara] = ladosAuto(a, b)
  const ladoDe = fromSide ?? autoDe
  const ladoPara = toSide ?? autoPara
  const p1 = ancora(a, ladoDe)
  const p2 = ancora(b, ladoPara)
  const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y)
  const c = curvada ? Math.max(30, dist / 4) : Math.max(40, dist / 2)
  return { p1, p2, c, d1: DIRECAO[ladoDe], d2: DIRECAO[ladoPara] }
}

function caminho(a: No, b: No, fromSide: Lado | null, toSide: Lado | null, bend: Curvatura | null = null) {
  const { p1, p2, c, d1, d2 } = geometria(a, b, fromSide, toSide, bend !== null)
  // a curvatura escolhida desloca os DOIS pontos de controle: a saida e a
  // entrada continuam perpendiculares as faces, so a barriga muda
  const o = bend ?? { x: 0, y: 0 }
  return `M ${p1.x} ${p1.y} C ${p1.x + d1.x * c + o.x} ${p1.y + d1.y * c + o.y}, ${p2.x + d2.x * c + o.x} ${p2.y + d2.y * c + o.y}, ${p2.x} ${p2.y}`
}

/**
 * Ponto da curva em t = 0,5 — onde fica a ALCA de curvatura. Para a cubica
 * com os controles deslocados por O: M + 3/8·c·(d1+d2) + 3/4·O, com M o meio
 * da reta entre as ancoras. A inversa (`curvaturaPara`) e exata: a alca cai
 * onde a pessoa soltou.
 */
function pontoMedio(a: No, b: No, fromSide: Lado | null, toSide: Lado | null, bend: Curvatura | null) {
  const { p1, p2, c, d1, d2 } = geometria(a, b, fromSide, toSide, bend !== null)
  const o = bend ?? { x: 0, y: 0 }
  return {
    x: (p1.x + p2.x) / 2 + (3 / 8) * c * (d1.x + d2.x) + (3 / 4) * o.x,
    y: (p1.y + p2.y) / 2 + (3 / 8) * c * (d1.y + d2.y) + (3 / 4) * o.y,
  }
}

/** A curvatura que poe o meio da curva exatamente em `p`. */
function curvaturaPara(a: No, b: No, fromSide: Lado | null, toSide: Lado | null, p: { x: number; y: number }): Curvatura {
  const { p1, p2, c, d1, d2 } = geometria(a, b, fromSide, toSide, true)
  return {
    x: Math.round(((p.x - (p1.x + p2.x) / 2 - (3 / 8) * c * (d1.x + d2.x)) * 4) / 3),
    y: Math.round(((p.y - (p1.y + p2.y) / 2 - (3 / 8) * c * (d1.y + d2.y)) * 4) / 3),
  }
}

/**
 * Onde fica a ALCA de religar: um pouco PARA FORA da caixa, na direcao de
 * entrada da seta — assim nao briga com a porta nem some debaixo do cartao.
 */
function pontaFinal(a: No, b: No, fromSide: Lado | null, toSide: Lado | null) {
  const [, autoPara] = ladosAuto(a, b)
  const lado = toSide ?? autoPara
  const p = ancora(b, lado)
  const d = DIRECAO[lado]
  return { x: p.x + d.x * 16, y: p.y + d.y * 16 }
}

/** Alca da ORIGEM da ligacao — tambem da para pegar e levar aonde quiser. */
function pontaInicial(a: No, b: No, fromSide: Lado | null) {
  const [autoDe] = ladosAuto(a, b)
  const lado = fromSide ?? autoDe
  const p = ancora(a, lado)
  const d = DIRECAO[lado]
  return { x: p.x + d.x * 16, y: p.y + d.y * 16 }
}

export function FlowDesigner({
  flowId,
  onClose,
  destacarFlowStepId = null,
  destacarItemCodigo = null,
}: {
  flowId: string
  onClose: () => void
  /** No (tb_flow_step.id) DESTACADO — "o item esta nesta etapa" (2026-08-25). */
  destacarFlowStepId?: string | null
  /** Codigo do item (WI-xxxx) mostrado no selo sobre a etapa destacada. */
  destacarItemCodigo?: string | null
}) {
  const toast = useToast()
  const qc = useQueryClient()
  // confirmacao destrutiva (remocao de etapa usada por lanes de quadros)
  const confirm = useConfirm()

  const grafo = useQuery({
    queryKey: ['flow-graph', flowId],
    queryFn: async () => (await api.flows[':id'].$get({ param: { id: flowId } })).json(),
  })
  // etapa em que o ITEM esta (aberto pelo icone do card/detalhe): destacada
  // no desenho com cor diferente
  const etapaDestacada = destacarFlowStepId
    ? (grafo.data?.steps.find((s) => s.id === destacarFlowStepId)?.processStepId ?? null)
    : null
  const catalogo = useQuery({
    queryKey: ['process-steps'],
    queryFn: async () => (await api.flows.steps.$get()).json(),
  })

  const [nos, setNos] = useState<No[]>([])
  const [arestas, setArestas] = useState<Aresta[]>([])
  // tamanho vivo do canvas (cresce ao atingir as bordas; nao entra no dirty)
  const [tamanho, setTamanho] = useState(TAMANHO_MIN)
  // centro (coords do canvas) a focar apos carregar — aplicado num layout effect
  const focoInicialRef = useRef<{ cx: number; cy: number } | null>(null)
  // compensacao de scroll pendente apos deslocar o desenho (expansao esq/topo)
  const shiftPendenteRef = useRef<{ dx: number; dy: number } | null>(null)
  // janela flutuante de INCLUIR ETAPA EXISTENTE no fluxo (pedido do usuario
  // 2026-08-15): so habilita as etapas que ainda NAO estao neste fluxo
  const [pickerAberto, setPickerAberto] = useState(false)

  // clique FORA fecha a janela — sem backdrop, para o canvas continuar
  // rolando/pannando normalmente com ela aberta
  useEffect(() => {
    if (!pickerAberto) return
    const aoClicarFora = (e: PointerEvent) => {
      const alvo = e.target as HTMLElement | null
      if (alvo?.closest('.flowpick--float') || alvo?.closest('[data-picker-toggle]')) return
      setPickerAberto(false)
    }
    document.addEventListener('pointerdown', aoClicarFora)
    return () => document.removeEventListener('pointerdown', aoClicarFora)
  }, [pickerAberto])
  /** Snapshot carregado — base do dirty check. */
  const [base, setBase] = useState<string | null>(null)
  const [arestaSelecionada, setArestaSelecionada] = useState<Aresta | null>(null)
  /** Etapa selecionada no canvas (clique no cartao; clique no fundo limpa). */
  const [noSelecionado, setNoSelecionado] = useState<string | null>(null)
  /** Canvas maximizado: overlay usando toda a area de trabalho (Esc restaura). */
  const [maximizado, setMaximizado] = useState(false)
  /**
   * VISUALIZACAO x EDICAO (pedido do usuario 2026-08-24): o designer abre so
   * para VER — caixas e setas, sem portas, sem lixeira, sem arrastar nem
   * renomear. O lapis na barra entra na edicao; o olho volta a visualizar.
   */
  const [modoEdicao, setModoEdicao] = useState(false)
  /** Renomear a etapa NA CAIXA (pedido do usuario): clique no nome do cartao
   *  ja selecionado abre o input; Enter/blur salva no CADASTRO, Esc cancela. */
  const [renomeando, setRenomeando] = useState<{ id: string; valor: string } | null>(null)

  /**
   * Ícone da ETAPA (2026-08-30). Vale no cadastro inteiro, como o nome: a mesma
   * etapa em outro fluxo passa a mostrar o mesmo ícone — e é ele que aparece na
   * lista de etapas e no cronograma.
   */
  const [iconeDe, setIconeDe] = useState<string | null>(null)
  // status ativos para a configuracao "status ao entrar na etapa"
  const bootstrap = useBootstrap()
  const t = useT()
  const salvarConfigEtapa = useMutation({
    mutationFn: async (input: {
      flowStepId: string
      onEnterStatusId?: string | null
      isInitial?: boolean
      isFinal?: boolean
      startX?: number | null
      startY?: number | null
      finalX?: number | null
      finalY?: number | null
    }) => {
      const { flowStepId, ...json } = input
      return (await api.flows.etapas[':flowStepId'].$patch({ param: { flowStepId }, json })).json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['flow-graph', flowId] })
      qc.invalidateQueries({ queryKey: ['flow-nodes'] })
      toast.push({ tone: 'success', message: 'Configuração da etapa salva.' })
    },
    onError: (e) =>
      toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao salvar a configuração.' }),
  })
  const trocarIcone = useMutation({
    mutationFn: async (vars: { id: string; icon: string }) =>
      (await api.flows.steps[':id'].$patch({ param: { id: vars.id }, json: { icon: vars.icon } as never })).json(),
    onSuccess: (dados) => {
      void qc.invalidateQueries({ queryKey: ['process-steps'] })
      void qc.invalidateQueries({ queryKey: ['flows'] })
      void qc.invalidateQueries({ queryKey: ['flow-nodes'] })
      const etapa = (dados as { step?: { id: string; icon: string } }).step
      if (etapa) {
        setNos((atual) => atual.map((x) => (x.processStepId === etapa.id ? { ...x, icon: etapa.icon } : x)))
      }
      setIconeDe(null)
    },
    onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao trocar o ícone.' }),
  })

  const renomearEtapa = useMutation({
    mutationFn: async (vars: { id: string; name: string }) =>
      (await api.flows.steps[':id'].$patch({ param: { id: vars.id }, json: { name: vars.name } })).json(),
    onSuccess: (dados) => {
      void qc.invalidateQueries({ queryKey: ['process-steps'] })
      void qc.invalidateQueries({ queryKey: ['flows'] })
      if (dados.step) {
        const etapa = dados.step
        setNos((atual) => atual.map((n) => (n.processStepId === etapa.id ? { ...n, name: etapa.name } : n)))
        toast.push({ tone: 'success', message: `Etapa renomeada para “${etapa.name}”.` })
      }
      setRenomeando(null)
    },
    onError: (erro) => {
      setRenomeando(null)
      toast.push({
        tone: 'danger',
        message: erro instanceof Error && erro.message ? erro.message : 'Não foi possível renomear a etapa.',
      })
    },
  })

  const confirmarRenomeio = () => {
    if (!renomeando) return
    const nome = renomeando.valor.trim()
    const atual = nos.find((n) => n.processStepId === renomeando.id)
    if (nome.length < 2 || !atual || nome === atual.name) {
      setRenomeando(null)
      return
    }
    renomearEtapa.mutate({ id: renomeando.id, name: nome })
  }

  const etapaDoIcone = iconeDe ? nos.find((x) => x.processStepId === iconeDe) : undefined
  /** Dados SALVOS da etapa aberta na modal (nó novo ainda não tem etapa no banco). */
  const etapaSalva = iconeDe ? grafo.data?.steps.find((x) => x.processStepId === iconeDe) : undefined

  const innerRef = useRef<HTMLDivElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const dragRef = useRef<{ id: string; dx: number; dy: number; startX: number; startY: number; moveu: boolean; peloNome: boolean } | null>(null)
  const [arrastando, setArrastando] = useState<string | null>(null)
  /** Onde a ponta na mao vai cair se soltar agora (porta/lado destacados). */
  type AlvoDaLigacao = { id: string; lado: Lado; exato: boolean } | null
  const [ligando, setLigando] = useState<{
    from: string
    fromSide: Lado
    x: number
    y: number
    sx: number
    sy: number
    alvo: AlvoDaLigacao
  } | null>(null)
  /** Religando: a ponta da seta de uma ligacao existente sendo arrastada. */
  const [religando, setReligando] = useState<{
    original: Aresta
    /** Qual extremidade esta na mao: 'to' = ponta da seta, 'from' = origem. */
    ponta: 'from' | 'to'
    x: number
    y: number
    sx: number
    sy: number
    alvo: AlvoDaLigacao
  } | null>(null)
  /**
   * INICIO/FIM DE FLUXO (2026-09-02): segurar no VAZIO e arrastar puxa uma
   * linha do ponto clicado; soltar num cartao pergunta se aquele ponto marca
   * um INICIO (itens podem comecar pela etapa) ou um FIM de fluxo, e o
   * simbolo fica desenhado ali, conectado a etapa.
   */
  const [ligandoDoVazio, setLigandoDoVazio] = useState<{ x0: number; y0: number; x: number; y: number } | null>(null)
  /** Curvando: a alca do meio de uma ligacao selecionada esta na mao (SPEC-20261103-2312). */
  const [curvando, setCurvando] = useState<{ from: string; to: string } | null>(null)
  const [perguntaSimbolo, setPerguntaSimbolo] = useState<{ processStepId: string; x: number; y: number } | null>(null)

  /** Pan: segurar o botao no FUNDO e arrastar move a area de desenho. */
  const panRef = useRef<{ startX: number; startY: number; scrollLeft: number; scrollTop: number } | null>(null)
  const [pannando, setPannando] = useState(false)

  // Zoom com o scroll do mouse (pedido do usuario, 2026-08-15). O valor vive
  // tambem num ref porque o listener de wheel e NATIVO (passive: false — o
  // preventDefault segura o scroll da pagina) e nao pode fechar sobre estado.
  const [zoom, setZoom] = useState(1)
  const zoomRef = useRef(1)
  /** Compensacao de scroll pendente — aplicada ANTES do paint (sem piscar). */
  const focoPendenteRef = useRef<{ mx: number; my: number; mundoX: number; mundoY: number } | null>(null)
  const aplicarZoom = (novo: number, foco?: { clientX: number; clientY: number }) => {
    const limitado = Math.min(2, Math.max(0.3, novo))
    const container = containerRef.current
    if (container) {
      // mantem o ponto sob o cursor (ou o centro) parado durante o zoom
      const r = container.getBoundingClientRect()
      const mx = foco ? foco.clientX - r.left : r.width / 2
      const my = foco ? foco.clientY - r.top : r.height / 2
      focoPendenteRef.current = {
        mx,
        my,
        mundoX: (container.scrollLeft + mx) / zoomRef.current,
        mundoY: (container.scrollTop + my) / zoomRef.current,
      }
    }
    zoomRef.current = limitado
    setZoom(limitado)
  }

  // useLayoutEffect roda apos o DOM escalar e ANTES do navegador pintar:
  // o scroll compensa no mesmo frame e o zoom nao pisca.
  useLayoutEffect(() => {
    const container = containerRef.current
    const foco = focoPendenteRef.current
    if (!container || !foco) return
    focoPendenteRef.current = null
    container.scrollLeft = foco.mundoX * zoom - foco.mx
    container.scrollTop = foco.mundoY * zoom - foco.my
  }, [zoom])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const aoRolar = (e: WheelEvent) => {
      // Zoom SO com Ctrl + scroll (pedido do usuario): scroll puro continua
      // rolando o canvas. Pinça de trackpad tambem chega com ctrlKey=true.
      if (!e.ctrlKey) return
      e.preventDefault()
      const fator = e.deltaY > 0 ? 1 / 1.1 : 1.1
      aplicarZoom(zoomRef.current * fator, e)
    }
    container.addEventListener('wheel', aoRolar, { passive: false })
    return () => container.removeEventListener('wheel', aoRolar)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grafo.isPending])

  const normalizado = (n: No[], a: Aresta[]) =>
    JSON.stringify({
      nos: [...n]
        .map((x) => ({ id: x.processStepId, x: x.x, y: x.y }))
        .sort((p, q) => p.id.localeCompare(q.id)),
      arestas: [...a]
        .map((x) => `${x.from}>${x.to}:${x.fromSide ?? 'auto'}:${x.toSide ?? 'auto'}:${x.bend ? `${x.bend.x},${x.bend.y}` : 'auto'}`)
        .sort(),
    })

  useEffect(() => {
    if (grafo.data) {
      const carregados = grafo.data.steps.map((s) => ({
        processStepId: s.processStepId,
        name: s.name,
        color: s.color,
        icon: (s as { icon?: string | null }).icon ?? null,
        x: s.positionX,
        y: s.positionY,
      }))
      // as ligacoes vem por id de NO; convertem para processStepId (chave local)
      const porNo = new Map(grafo.data.steps.map((s) => [s.id, s.processStepId]))
      const lig = grafo.data.transitions
        .map((t) => ({
          from: porNo.get(t.from) ?? '',
          to: porNo.get(t.to) ?? '',
          fromSide: (t.fromSide ?? null) as Lado | null,
          toSide: (t.toSide ?? null) as Lado | null,
          bend: t.bendX !== null && t.bendX !== undefined && t.bendY !== null && t.bendY !== undefined ? { x: t.bendX, y: t.bendY } : null,
        }))
        .filter((t) => t.from !== '' && t.to !== '')
      setNos(carregados)
      setArestas(lig)
      setBase(normalizado(carregados, lig))
      setArestaSelecionada(null)

      // canvas dimensionado para caber o desenho, e a vista ABRE CENTRADA:
      // vazio = centro do canvas; com etapas = centro do desenho
      const maxX = carregados.reduce((m, n) => Math.max(m, n.x + larguraDe(n)), 0)
      const maxY = carregados.reduce((m, n) => Math.max(m, n.y + ALTURA_NO), 0)
      const alvo = {
        w: Math.min(TAMANHO_MAX, Math.max(TAMANHO_MIN.w, maxX + 1200)),
        h: Math.min(TAMANHO_MAX, Math.max(TAMANHO_MIN.h, maxY + 800)),
      }
      setTamanho(alvo)
      if (carregados.length === 0) {
        focoInicialRef.current = { cx: alvo.w / 2, cy: alvo.h / 2 }
      } else {
        const minX = Math.min(...carregados.map((n) => n.x))
        const minY = Math.min(...carregados.map((n) => n.y))
        focoInicialRef.current = { cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 }
      }
      // aberto pelo item: centra a vista na etapa destacada
      const destacado = destacarFlowStepId ? grafo.data.steps.find((s) => s.id === destacarFlowStepId) : undefined
      if (destacado) {
        focoInicialRef.current = { cx: destacado.positionX + LARGURA_NO / 2, cy: destacado.positionY + ALTURA_NO / 2 }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grafo.data])

  // aplica o foco inicial e a compensacao de shift DEPOIS do commit (o DOM ja
  // esta com o tamanho novo — setar scroll antes seria clampado pelo maximo antigo)
  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return
    const foco = focoInicialRef.current
    if (foco) {
      focoInicialRef.current = null
      const z = zoomRef.current
      container.scrollLeft = Math.max(0, foco.cx * z - container.clientWidth / 2)
      container.scrollTop = Math.max(0, foco.cy * z - container.clientHeight / 2)
    }
    const shift = shiftPendenteRef.current
    if (shift) {
      shiftPendenteRef.current = null
      container.scrollLeft += shift.dx * zoomRef.current
      container.scrollTop += shift.dy * zoomRef.current
    }
  })

  const alterado = base !== null && normalizado(nos, arestas) !== base

  const montarPayload = (n: No[], a: Aresta[]) => ({
    steps: n.map((x) => ({
      processStepId: x.processStepId,
      positionX: Math.round(x.x),
      positionY: Math.round(x.y),
    })),
    transitions: a,
  })

  const invalidarGrafo = () => {
    void qc.invalidateQueries({ queryKey: ['flow-graph', flowId] })
    void qc.invalidateQueries({ queryKey: ['flows'] })
    void qc.invalidateQueries({ queryKey: ['process-steps'] })
  }

  const salvar = useMutation({
    mutationFn: async () =>
      (await api.flows[':id'].graph.$put({ param: { id: flowId }, json: montarPayload(nos, arestas) })).json(),
    onSuccess: () => {
      invalidarGrafo()
      toast.push({ tone: 'success', message: 'Desenho do fluxo salvo.' })
    },
    onError: (erro) =>
      toast.push({
        tone: 'danger',
        message: erro instanceof Error && erro.message ? erro.message : 'Não foi possível salvar o desenho.',
      }),
  })

  /**
   * A LIXEIRA persiste NA HORA (pedido do usuario): salva o desenho ja sem a
   * etapa — no servidor a exclusao do cadastro (quando era o unico fluxo) e a
   * varredura de orfas acontecem no mesmo ato. Se uma LANE usa a etapa (409),
   * o desenho volta como estava e um aviso lista os quadros pedindo
   * CONFIRMACAO: confirmando, as lanes ficam sem etapa e a exclusao segue
   * (pedido do usuario em 2026-08-15).
   */
  const removerESalvar = useMutation({
    mutationFn: async (vars: {
      nosNovos: No[]
      arestasNovas: Aresta[]
      anterior: { nos: No[]; arestas: Aresta[] }
      detachLanes?: boolean
    }) =>
      (
        await api.flows[':id'].graph.$put({
          param: { id: flowId },
          json: { ...montarPayload(vars.nosNovos, vars.arestasNovas), detachLanes: vars.detachLanes ?? false },
        })
      ).json(),
    onSuccess: () => {
      invalidarGrafo()
      toast.push({ tone: 'success', message: 'Etapa removida do fluxo — cadastro e órfãs atualizados.' })
    },
    onError: (erro, vars) => {
      // devolve o desenho como estava enquanto o usuario decide (ou desiste)
      setNos(vars.anterior.nos)
      setArestas(vars.anterior.arestas)
      if (erro instanceof OrbindApiError && erro.code === 'FLOW_STEP_IN_USE') {
        confirm.ask(
          `${erro.message} Confirmando, a(s) lane(s) ficam SEM etapa e a remoção segue.`,
          () => {
            setNos(vars.nosNovos)
            setArestas(vars.arestasNovas)
            removerESalvar.mutate({ ...vars, detachLanes: true })
          },
        )
        return
      }
      toast.push({
        tone: 'danger',
        message: erro instanceof Error && erro.message ? erro.message : 'Não foi possível remover a etapa.',
      })
    },
  })

  /** Etapa que nunca entrou em fluxo nenhum (no ainda nao salvo): apaga direto. */
  const excluirEtapaOrfa = useMutation({
    mutationFn: async (id: string) => (await api.flows.steps[':id'].$delete({ param: { id } })).json(),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['process-steps'] }),
    onError: () => void qc.invalidateQueries({ queryKey: ['process-steps'] }),
  })

  /**
   * "Etapa N+1", onde N e o MAIOR numero de "Etapa X" ja existente (pedido do
   * usuario): olha o CATALOGO inteiro (nome unico no workspace) e os nos
   * locais, pega o maior e soma um — sempre o proximo, sem reaproveitar
   * numeros de etapas excluidas.
   */
  const maiorNumeroDeEtapa = () => {
    const nomes = [...(catalogo.data?.steps ?? []).map((s) => s.name), ...nos.map((n) => n.name)]
    let maior = 0
    for (const nome of nomes) {
      const m = /^etapa\s+(\d+)$/i.exec(nome.trim())
      if (m) maior = Math.max(maior, Number(m[1]))
    }
    return maior
  }

  /**
   * Cria "Etapa N" no cadastro; se o nome JA EXISTIR no workspace (409 — o
   * catalogo local pode estar defasado), incrementa e tenta o proximo numero
   * ate achar um livre (pedido do usuario em 2026-08-15).
   */
  const criarEtapaLivre = async () => {
    let numero = maiorNumeroDeEtapa() + 1
    for (let tentativa = 0; tentativa < 100; tentativa++, numero++) {
      try {
        const resp = await api.flows.steps.$post({ json: { name: `Etapa ${numero}` } })
        const dados = (await resp.json()) as { step?: { id: string; name: string; color: string | null } }
        if (dados.step) return dados.step
        throw new Error('Não foi possível criar a etapa.')
      } catch (erro) {
        // nome ocupado (o catalogo local pode estar defasado): proximo numero
        if (erro instanceof OrbindApiError && erro.status === 409) continue
        throw erro
      }
    }
    throw new Error('Não foi possível encontrar um número livre para a etapa.')
  }

  const criarEtapa = useMutation({
    mutationFn: criarEtapaLivre,
    onSuccess: (etapa) => {
      void qc.invalidateQueries({ queryKey: ['process-steps'] })
      incluirNo({ id: etapa.id, name: etapa.name, color: etapa.color })

      toast.push({ tone: 'success', message: `Etapa “${etapa.name}” criada e incluída no fluxo.` })
    },
    onError: (erro) =>
      toast.push({
        tone: 'danger',
        message: erro instanceof Error && erro.message ? erro.message : 'Não foi possível criar a etapa.',
      }),
  })

  /**
   * Soltar a linha no VAZIO cria uma etapa nova (pedido do usuario): nasce no
   * cadastro de etapas (independente do fluxo), aparece onde o ponteiro
   * soltou e ja entra ligada na sequencia. O nome e provisorio — troca depois
   * no cadastro.
   */
  const criarEtapaNaPonta = useMutation({
    mutationFn: async (_vars: { from: string; fromSide: Lado | null; x: number; y: number }) => criarEtapaLivre(),
    onSuccess: (etapa, vars) => {
      void qc.invalidateQueries({ queryKey: ['process-steps'] })
      setNos((atual) => [
        ...atual,
        {
          processStepId: etapa.id,
          name: etapa.name,
          color: etapa.color,
          x: vars.x,
          y: vars.y,
        },
      ])
      setArestas((atual) => [...atual, { from: vars.from, to: etapa.id, fromSide: vars.fromSide, toSide: null, bend: null }])
      toast.push({ tone: 'success', message: `“${etapa.name}” criada e ligada — renomeie no cadastro de etapas quando quiser.` })
    },
    onError: (erro) =>
      toast.push({
        tone: 'danger',
        message: erro instanceof Error && erro.message ? erro.message : 'Não foi possível criar a etapa.',
      }),
  })

  /**
   * A etapa nova nasce no CENTRO da area visivel do canvas (pedido do
   * usuario) — considera o scroll e o zoom atuais; se ja houver caixa ali,
   * desloca em escadinha para nao empilhar.
   */
  const incluirNo = (etapa: { id: string; name: string; color: string | null }) => {
    const container = containerRef.current
    let x = 120
    let y = 120
    if (container) {
      x = Math.max(0, Math.round((container.scrollLeft + container.clientWidth / 2) / zoomRef.current - LARGURA_NO / 2))
      y = Math.max(0, Math.round((container.scrollTop + container.clientHeight / 2) / zoomRef.current - ALTURA_NO / 2))
    }
    setNos((atual) => {
      if (atual.some((n) => n.processStepId === etapa.id)) return atual
      let px = x
      let py = y
      while (atual.some((n) => Math.abs(n.x - px) < 32 && Math.abs(n.y - py) < 32)) {
        px += 32
        py += 32
      }
      return [
        ...atual,
        {
          processStepId: etapa.id,
          name: etapa.name,
          color: etapa.color,
          x: px,
          y: py,
        },
      ]
    })
  }

  const removerNo = (id: string) => {
    const anterior = { nos, arestas }
    const nosNovos = nos.filter((n) => n.processStepId !== id)
    const arestasNovas = arestas.filter((a) => a.from !== id && a.to !== id)
    setNos(nosNovos)
    setArestas(arestasNovas)
    setArestaSelecionada(null)
    setNoSelecionado((atual) => (atual === id ? null : atual))

    const estavaSalvo = (grafo.data?.steps ?? []).some((s) => s.processStepId === id)
    if (estavaSalvo) {
      // persiste ja: exclusao do cadastro + varredura acontecem agora
      removerESalvar.mutate({ nosNovos, arestasNovas, anterior })
    } else {
      // no nunca salvo em fluxo nenhum: a etapa orfa sai do cadastro direto
      const passo = (catalogo.data?.steps ?? []).find((s) => s.id === id)
      if (!passo || passo.flowCount === 0) excluirEtapaOrfa.mutate(id)
    }
  }

  // Delete/Backspace remove o selecionado (etapa ou ligacao) — so quando o
  // foco nao esta num campo de texto (o painel de adicionar etapa tem inputs).
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPickerAberto(false)
        setMaximizado(false)
        return
      }
      if (e.key !== 'Delete' && e.key !== 'Backspace') return
      if (!modoEdicao) return
      const alvo = document.activeElement as HTMLElement | null
      if (alvo && (alvo.tagName === 'INPUT' || alvo.tagName === 'TEXTAREA' || alvo.isContentEditable)) return
      if (noSelecionado) {
        e.preventDefault()
        removerNo(noSelecionado)
      } else if (arestaSelecionada) {
        e.preventDefault()
        setArestas((atual) =>
          atual.filter((a) => !(a.from === arestaSelecionada.from && a.to === arestaSelecionada.to)),
        )
        setArestaSelecionada(null)
      }
    }
    window.addEventListener('keydown', aoTeclar)
    return () => window.removeEventListener('keydown', aoTeclar)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noSelecionado, arestaSelecionada, modoEdicao])

  const mudarNo = (id: string, patch: Partial<No>) =>
    setNos((atual) => atual.map((n) => (n.processStepId === id ? { ...n, ...patch } : n)))

  /** Coordenadas do ponteiro no sistema do canvas (considera scroll e ZOOM). */
  const pontoNoCanvas = (e: { clientX: number; clientY: number }) => {
    const el = innerRef.current!
    // o rect ja vem escalado pelo transform — dividir devolve o mundo 1:1
    const r = el.getBoundingClientRect()
    return { x: (e.clientX - r.left) / zoomRef.current, y: (e.clientY - r.top) / zoomRef.current }
  }

  /**
   * Cresce o canvas quando uma etapa chega perto das bordas. Direita/baixo so
   * aumentam o tamanho; esquerda/topo DESLOCAM o desenho inteiro (e compensam
   * o scroll no layout effect) para abrir espaco.
   */
  const expandirSePreciso = (x: number, y: number, largura = LARGURA_NO) => {
    setTamanho((t) => {
      let { w, h } = t
      if (x + largura > w - MARGEM_BORDA && w < TAMANHO_MAX) w += PASSO_EXPANSAO
      if (y + ALTURA_NO > h - MARGEM_BORDA && h < TAMANHO_MAX) h += PASSO_EXPANSAO
      return w !== t.w || h !== t.h ? { w, h } : t
    })
    if (x < MARGEM_BORDA) {
      setNos((atual) => atual.map((n) => ({ ...n, x: n.x + PASSO_EXPANSAO })))
      setTamanho((t) => ({ ...t, w: Math.min(TAMANHO_MAX, t.w + PASSO_EXPANSAO) }))
      shiftPendenteRef.current = { dx: (shiftPendenteRef.current?.dx ?? 0) + PASSO_EXPANSAO, dy: shiftPendenteRef.current?.dy ?? 0 }
    }
    if (y < MARGEM_BORDA) {
      setNos((atual) => atual.map((n) => ({ ...n, y: n.y + PASSO_EXPANSAO })))
      setTamanho((t) => ({ ...t, h: Math.min(TAMANHO_MAX, t.h + PASSO_EXPANSAO) }))
      shiftPendenteRef.current = { dx: shiftPendenteRef.current?.dx ?? 0, dy: (shiftPendenteRef.current?.dy ?? 0) + PASSO_EXPANSAO }
    }
  }

  /** Pan chegou na borda direita/baixo? O canvas cresce e o passeio continua. */
  const expandirPeloPan = (scrollLeft: number, scrollTop: number) => {
    const container = containerRef.current
    if (!container) return
    const z = zoomRef.current
    setTamanho((t) => {
      let { w, h } = t
      if (scrollLeft + container.clientWidth > w * z - 8 && w < TAMANHO_MAX) w += PASSO_EXPANSAO
      if (scrollTop + container.clientHeight > h * z - 8 && h < TAMANHO_MAX) h += PASSO_EXPANSAO
      return w !== t.w || h !== t.h ? { w, h } : t
    })
  }

  /** Arma o pan (segurar e arrastar move a area) capturando o ponteiro. */
  const armarPan = (e: React.PointerEvent, captor: HTMLElement) => {
    const container = containerRef.current
    if (!container) return
    panRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop,
    }
    setPannando(true)
    captor.setPointerCapture(e.pointerId)
  }

  const aoMoverPonteiro = (e: React.PointerEvent) => {
    if (panRef.current) {
      const container = containerRef.current
      if (container) {
        const destinoL = panRef.current.scrollLeft - (e.clientX - panRef.current.startX)
        const destinoT = panRef.current.scrollTop - (e.clientY - panRef.current.startY)
        container.scrollLeft = destinoL
        container.scrollTop = destinoT
        expandirPeloPan(destinoL, destinoT)
      }
      return
    }
    if (dragRef.current) {
      const p = pontoNoCanvas(e)
      // so vira arraste depois de mover de verdade — o clique parado no nome
      // e o gesto de RENOMEAR (resolvido no soltar)
      if (Math.hypot(p.x - dragRef.current.startX, p.y - dragRef.current.startY) > 3) {
        dragRef.current.moveu = true
      }
      if (dragRef.current.moveu) {
        const nx = Math.max(0, p.x - dragRef.current.dx)
        const ny = Math.max(0, p.y - dragRef.current.dy)
        mudarNo(dragRef.current.id, { x: nx, y: ny })
        const noArrastado = porId.get(dragRef.current.id)
        expandirSePreciso(nx, ny, noArrastado ? larguraDe(noArrastado) : LARGURA_NO)
      }
    } else if (ligando) {
      const p = pontoNoCanvas(e)
      setLigando({ ...ligando, x: p.x, y: p.y, alvo: alvoDaLigacao(nos, p, ligando.from) })
    } else if (religando) {
      const p = pontoNoCanvas(e)
      const fixa = religando.ponta === 'to' ? religando.original.from : religando.original.to
      setReligando({ ...religando, x: p.x, y: p.y, alvo: alvoDaLigacao(nos, p, fixa) })
    } else if (ligandoDoVazio) {
      const p = pontoNoCanvas(e)
      setLigandoDoVazio({ ...ligandoDoVazio, x: p.x, y: p.y })
    } else if (curvando) {
      // o meio da curva segue o ponteiro: a curvatura e recalculada para
      // a alca cair exatamente onde a pessoa esta segurando
      const p = pontoNoCanvas(e)
      const de = porId.get(curvando.from)
      const para = porId.get(curvando.to)
      if (de && para) {
        setArestas((atual) =>
          atual.map((x) =>
            x.from === curvando.from && x.to === curvando.to ? { ...x, bend: curvaturaPara(de, para, x.fromSide, x.toSide, p) } : x,
          ),
        )
      }
    }
  }

  const aoSoltarPonteiro = (e: React.PointerEvent) => {
    if (panRef.current) {
      panRef.current = null
      setPannando(false)
    }
    if (curvando) setCurvando(null)
    if (dragRef.current) {
      // clique parado sobre o NOME = renomear direto na caixa
      if (!dragRef.current.moveu && dragRef.current.peloNome) {
        const alvo = nos.find((n) => n.processStepId === dragRef.current!.id)
        if (alvo) setRenomeando({ id: alvo.processStepId, valor: alvo.name })
      }
      dragRef.current = null
      setArrastando(null)
    }
    if (ligando) {
      // o alvo e resolvido por GEOMETRIA no ultimo movimento (a porta mirada
      // ou o cartao sob o ponteiro) — nunca pelo elemento que recebeu o up
      const alvo = alvoDaLigacao(nos, pontoNoCanvas(e), ligando.from) ?? ligando.alvo
      if (alvo) {
        setArestas((atual) =>
          atual.some((a) => a.from === ligando.from && a.to === alvo.id)
            ? atual
            : [...atual, { from: ligando.from, to: alvo.id, fromSide: ligando.fromSide, toSide: alvo.lado, bend: null }],
        )
      } else if (!criarEtapaNaPonta.isPending) {
        // soltou no vazio: cria uma etapa nova ali e liga na sequencia — o
        // lado ESQUERDO da caixa comeca no ponto do soltar (pedido do usuario)
        const p = pontoNoCanvas(e)
        criarEtapaNaPonta.mutate({
          from: ligando.from,
          fromSide: ligando.fromSide,
          x: Math.max(0, Math.round(p.x)),
          y: Math.max(0, Math.round(p.y - ALTURA_NO / 2)),
        })
      }
      setLigando(null)
    }
    if (ligandoDoVazio) {
      const alvo = (e.target as HTMLElement).closest('[data-no]') as HTMLElement | null
      const destino = alvo?.dataset.no
      if (destino) {
        setPerguntaSimbolo({
          processStepId: destino,
          x: Math.round(ligandoDoVazio.x0),
          y: Math.round(ligandoDoVazio.y0),
        })
      }
      setLigandoDoVazio(null)
    }
    if (religando) {
      const original = religando.original
      // a outra extremidade fica fixa; nao pode soltar nela (viraria laco)
      const extremidadeFixa = religando.ponta === 'to' ? original.from : original.to
      const alvo = alvoDaLigacao(nos, pontoNoCanvas(e), extremidadeFixa) ?? religando.alvo
      if (alvo) {
        // reconecta a extremidade na PORTA mirada (ou no lado mais proximo de
        // onde soltou) — pode ser outra FACE da mesma etapa ou OUTRA etapa
        const nova: Aresta =
          religando.ponta === 'to'
            ? { from: original.from, to: alvo.id, fromSide: original.fromSide, toSide: alvo.lado, bend: original.bend }
            : { from: alvo.id, to: original.to, fromSide: alvo.lado, toSide: original.toSide, bend: original.bend }
        setArestas((atual) =>
          atual.some((a) => a.from === nova.from && a.to === nova.to)
            ? [...atual, original] // ja existe essa ligacao: devolve a original
            : [...atual, nova],
        )
      } else {
        // soltou no vazio ou na extremidade fixa: a ligacao volta como estava
        setArestas((atual) => [...atual, original])
      }
      setReligando(null)
    }
  }

  const porId = useMemo(() => new Map(nos.map((n) => [n.processStepId, n])), [nos])
  /** Porta/cartao destacados enquanto uma ponta esta na mao. */
  const alvoAtual = ligando?.alvo ?? religando?.alvo ?? null
  /** Com uma ponta na mao, as alcas e as portas nao podem engolir o soltar. */
  const pontaNaMao = ligando !== null || religando !== null || ligandoDoVazio !== null

  if (grafo.isPending) return <LoadingState variant="detail" label="Carregando o fluxo" />

  return (
    <FormScreen
      title="Fluxo"
      subtitle={grafo.data?.flow.name}
      onClose={onClose}
      dirty={alterado}
      saveLabel="Salvar desenho"
      saveDisabled={!alterado || salvar.isPending}
      saving={salvar.isPending}
      onSubmit={() => alterado && salvar.mutate()}
      hideFooter
    >
      <FormSection title="" collapsible={false}>
        <div className={`flowwrap${maximizado ? ' flowwrap--max' : ''}`}>
        <div className="flowcv-bar">
          {arestaSelecionada ? (
            <Button
              variant="danger"
              icon="fas fa-unlink"
              onClick={() => {
                setArestas((atual) =>
                  atual.filter((a) => !(a.from === arestaSelecionada.from && a.to === arestaSelecionada.to)),
                )
                setArestaSelecionada(null)
              }}
            >
              Remover ligação
            </Button>
          ) : null}
          <span className="flowcv-bar__spacer" />
          <span className="flowcv-bar__hint">
            Ctrl + scroll dá zoom · arraste o cartão para posicionar · arraste da bolinha azul até outra etapa
            para ligar · clique numa ligação para selecioná-la
          </span>
        </div>

        <div className="flowcvbox">
        {/* Cluster flutuante no canto superior direito (pedido do usuario):
            criar etapa no centro + zoom + maximizar (so o icone) */}
        <div className="flowcv__fabs">
          {modoEdicao ? (
            <>
              <button
                type="button"
                className="flowcv__fab flowcv__fab--primary"
                aria-label="Criar etapa no centro do desenho"
                title="Criar etapa no centro do desenho"
                disabled={criarEtapa.isPending}
                onClick={() => criarEtapa.mutate()}
              >
                <i className="fas fa-plus" aria-hidden />
              </button>
              <button
                type="button"
                className={`flowcv__fab${pickerAberto ? ' flowcv__fab--ativo' : ''}`}
                aria-label="Incluir etapa existente no fluxo"
                title="Incluir etapa existente no fluxo"
                data-picker-toggle
                onClick={() => setPickerAberto((v) => !v)}
              >
                <i className="fas fa-layer-group" aria-hidden />
              </button>
              <span className="flowcv__fabsep" aria-hidden />
            </>
          ) : null}
          <button type="button" className="flowcv__fab" aria-label="Diminuir zoom" title="Diminuir zoom"
            onClick={() => aplicarZoom(zoom / 1.2)}>
            <i className="fas fa-minus" aria-hidden />
          </button>
          <button type="button" className="flowcv__fab flowcv__fab--valor" title="Voltar para 100%"
            onClick={() => aplicarZoom(1)}>
            {Math.round(zoom * 100)}%
          </button>
          <button type="button" className="flowcv__fab" aria-label="Aumentar zoom" title="Aumentar zoom"
            onClick={() => aplicarZoom(zoom * 1.2)}>
            <i className="fas fa-plus" aria-hidden />
          </button>
          <span className="flowcv__fabsep" aria-hidden />
          <button
            type="button"
            className="flowcv__fab"
            aria-label={maximizado ? 'Restaurar (Esc)' : 'Maximizar — usar toda a área de trabalho'}
            title={maximizado ? 'Restaurar (Esc)' : 'Maximizar — usar toda a área de trabalho'}
            onClick={() => setMaximizado((v) => !v)}
          >
            <i className={maximizado ? 'fas fa-compress' : 'fas fa-expand'} aria-hidden />
          </button>
          <span className="flowcv__fabsep" aria-hidden />
          {/* alterna VISUALIZACAO x EDICAO (pedido do usuario) */}
          <button
            type="button"
            className={`flowcv__fab${modoEdicao ? ' flowcv__fab--ativo' : ''}`}
            aria-label={modoEdicao ? 'Concluir a edição (voltar a visualizar)' : 'Editar o desenho'}
            title={modoEdicao ? 'Concluir a edição (voltar a visualizar)' : 'Editar o desenho'}
            onClick={() => {
              setPickerAberto(false)
              setArestaSelecionada(null)
              setNoSelecionado(null)
              setRenomeando(null)
              setModoEdicao((v) => !v)
            }}
          >
            <i className={modoEdicao ? 'fas fa-eye' : 'fas fa-pen'} aria-hidden />
          </button>
          {modoEdicao || alterado ? (
            <button
              type="button"
              className="flowcv__fab flowcv__fab--primary flowcv__fab--save"
              aria-label="Salvar desenho"
              title={alterado ? 'Salvar o desenho do processo' : 'Tudo salvo — altere algo para salvar'}
              disabled={!alterado || salvar.isPending}
              onClick={() => salvar.mutate()}
            >
              <i className={salvar.isPending ? 'fas fa-circle-notch fa-spin' : 'fas fa-check'} aria-hidden />
              {alterado ? <span className="flowcv__fab-dot" aria-hidden /> : null}
            </button>
          ) : null}
        </div>
        {pickerAberto ? (
          <>
            {/* SEM backdrop de proposito: a janela e uma paleta — o canvas
                continua rolando/pannando com ela aberta (anti-modal) */}
            <div className="flowpick flowpick--float">
              <div className="flowpick__head">
                <span className="flowpick__title">Incluir etapa existente</span>
                <button
                  type="button"
                  className="flowpick__close"
                  aria-label="Fechar a lista de etapas"
                  onClick={() => setPickerAberto(false)}
                >
                  <i className="fas fa-times" aria-hidden />
                </button>
              </div>
              {(catalogo.data?.steps ?? []).length === 0 ? (
                <p className="flowpick__vazio">Nenhuma etapa cadastrada ainda — crie uma no botão +.</p>
              ) : (
                <div className="flowpick__list">
                  {(catalogo.data?.steps ?? [])
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
                    .map((s) => {
                      const jaNoFluxo = nos.some((n) => n.processStepId === s.id)
                      const fluxosDaEtapa = s.flowNames ?? []
                      return (
                        <button
                          key={s.id}
                          type="button"
                          className="flowpick__item"
                          disabled={jaNoFluxo}
                          title={jaNoFluxo ? 'Já está neste fluxo' : `Incluir “${s.name}” no desenho`}
                          onClick={() => incluirNo({ id: s.id, name: s.name, color: s.color })}
                        >
                          <span className="flowpick__dot" style={s.color ? { background: s.color } : undefined} />
                          <span className="flowpick__texto">
                            <span className="flowpick__nome">{s.name}</span>
                            {/* em quais fluxos a etapa esta (pedido do usuario) */}
                            {fluxosDaEtapa.length > 0 ? (
                              <span className="flowpick__fluxos">
                                {fluxosDaEtapa.length > 1 ? 'Fluxos: ' : 'Fluxo: '}
                                {fluxosDaEtapa.join(', ')}
                              </span>
                            ) : null}
                          </span>
                          {jaNoFluxo ? <span className="flowpick__ja">já neste fluxo</span> : null}
                        </button>
                      )
                    })}
                </div>
              )}
            </div>
          </>
        ) : null}
        <div
          className="flowcv"
          ref={containerRef}
          onPointerDown={(e) => {
            // pan tambem na area FORA do desenho (zoom pequeno deixa sobra
            // no container) — segurar e arrastar move de qualquer lugar
            const alvo = e.target as HTMLElement
            if (alvo !== containerRef.current && !alvo.classList?.contains('flowcv__scale')) return
            setNoSelecionado(null)
            setArestaSelecionada(null)
            armarPan(e, e.currentTarget as HTMLElement)
          }}
          onPointerMove={(e) => {
            // pans armados aqui nao passam pelo handler do inner
            if (panRef.current && (e.target === containerRef.current || (e.target as HTMLElement).classList?.contains('flowcv__scale'))) {
              const container = containerRef.current
              if (container) {
                const destinoL = panRef.current.scrollLeft - (e.clientX - panRef.current.startX)
                const destinoT = panRef.current.scrollTop - (e.clientY - panRef.current.startY)
                container.scrollLeft = destinoL
                container.scrollTop = destinoT
                expandirPeloPan(destinoL, destinoT)
              }
            }
          }}
          onPointerUp={() => {
            if (panRef.current) {
              panRef.current = null
              setPannando(false)
            }
          }}
        >
          {/* o wrapper dimensiona a area de scroll (transform nao afeta layout) */}
          <div className="flowcv__scale" style={{ width: tamanho.w * zoom, height: tamanho.h * zoom }}>
          <div
            ref={innerRef}
            className={`flowcv__inner${pannando ? ' flowcv__inner--pan' : ''}`}
            style={{ width: tamanho.w, height: tamanho.h, transform: `scale(${zoom})`, transformOrigin: '0 0' }}
            onPointerMove={aoMoverPonteiro}
            onPointerUp={aoSoltarPonteiro}
            onPointerDown={(e) => {
              // pan de QUALQUER lugar que nao seja interativo (no, porta,
              // alca da seta, input de renomear) — desenhar com conforto,
              // arrastando a area de onde quiser (pedido do usuario)
              const alvo = e.target as Element
              // na visualizacao a caixa nao arrasta: pan pega de qualquer lugar
              const interativo =
                (modoEdicao && alvo.closest('.flownode')) ||
                alvo.closest('.flowcv__tiphit') ||
                alvo.closest('.flowcv__tip') ||
                (alvo as HTMLElement).tagName === 'INPUT'
              if (interativo) return
              // clique no fundo (nao numa ligacao) tambem limpa as selecoes
              if (alvo.tagName !== 'path') {
                setNoSelecionado(null)
                setArestaSelecionada(null)
              }
              // EDICAO + botao esquerdo: puxa a linha de inicio/fim de fluxo
              // do ponto clicado (2026-09-02); pan continua no botao do meio
              // e na visualizacao
              if (modoEdicao && e.button === 0 && alvo.tagName !== 'path') {
                const p = pontoNoCanvas(e)
                setLigandoDoVazio({ x0: p.x, y0: p.y, x: p.x, y: p.y })
                return
              }
              armarPan(e, e.currentTarget as HTMLElement)
            }}
          >
            <svg className="flowcv__edges" aria-hidden>
              <defs>
                <marker id="flow-seta" markerWidth="9" markerHeight="9" refX="8" refY="4.5" orient="auto">
                  <path d="M0,0 L9,4.5 L0,9 z" fill="currentColor" />
                </marker>
              </defs>
              {arestas.map((a) => {
                const de = porId.get(a.from)
                const para = porId.get(a.to)
                if (!de || !para) return null
                const on = arestaSelecionada?.from === a.from && arestaSelecionada?.to === a.to
                return (
                  <path
                    key={`${a.from}>${a.to}`}
                    className={`flowcv__edge${on ? ' flowcv__edge--on' : ''}`}
                    d={caminho(de, para, a.fromSide, a.toSide, a.bend)}
                    markerEnd="url(#flow-seta)"
                    style={{ pointerEvents: modoEdicao ? 'stroke' : 'none' }}
                    onClick={() => modoEdicao && setArestaSelecionada(on ? null : a)}
                  />
                )
              })}
              {ligando
                ? (() => {
                    // com alvo, a previa JA E a curva final, encaixada na porta
                    // mirada — o que a pessoa ve e o que vai ficar
                    const de = porId.get(ligando.from)
                    const para = ligando.alvo ? porId.get(ligando.alvo.id) : undefined
                    const d =
                      de && para && ligando.alvo
                        ? caminho(de, para, ligando.fromSide, ligando.alvo.lado)
                        : `M ${ligando.sx} ${ligando.sy} L ${ligando.x} ${ligando.y}`
                    return (
                      <path
                        className={`flowcv__edge flowcv__edge--tmp${ligando.alvo ? ' flowcv__edge--tmp-alvo' : ''}`}
                        d={d}
                        markerEnd={ligando.alvo ? 'url(#flow-seta)' : undefined}
                      />
                    )
                  })()
                : null}
              {ligandoDoVazio ? (
                <path
                  className="flowcv__edge flowcv__edge--tmp"
                  d={`M ${ligandoDoVazio.x0} ${ligandoDoVazio.y0} L ${ligandoDoVazio.x} ${ligandoDoVazio.y}`}
                  markerEnd="url(#flow-seta)"
                />
              ) : null}
              {/* ligacoes dos simbolos de INICIO (simbolo -> etapa) e FIM
                  (etapa -> simbolo) */}
              {(grafo.data?.steps ?? []).map((sm) => {
                const no = porId.get(sm.processStepId)
                if (!no) return null
                const flags = sm as {
                  isInitial?: boolean
                  isFinal?: boolean
                  startPositionX?: number | null
                  startPositionY?: number | null
                  finalPositionX?: number | null
                  finalPositionY?: number | null
                }
                const linhas = [] as React.ReactNode[]
                if (flags.isInitial) {
                  const px = flags.startPositionX ?? no.x - 110
                  const py = flags.startPositionY ?? no.y + ALTURA_NO / 2
                  const lado = ladoMaisProximo(no, { x: px, y: py })
                  const a = ancora(no, lado)
                  const d = DIRECAO[lado]
                  const c = Math.max(30, Math.hypot(a.x - px, a.y - py) / 2)
                  linhas.push(
                    <path
                      key={`ini-${sm.processStepId}`}
                      className="flowcv__edge flowcv__edge--sym"
                      d={`M ${px} ${py} C ${px} ${py}, ${a.x + d.x * c} ${a.y + d.y * c}, ${a.x} ${a.y}`}
                      markerEnd="url(#flow-seta)"
                    />,
                  )
                }
                if (flags.isFinal) {
                  const px = flags.finalPositionX ?? no.x + larguraDe(no) + 110
                  const py = flags.finalPositionY ?? no.y + ALTURA_NO / 2
                  const lado = ladoMaisProximo(no, { x: px, y: py })
                  const a = ancora(no, lado)
                  const d = DIRECAO[lado]
                  const c = Math.max(30, Math.hypot(a.x - px, a.y - py) / 2)
                  linhas.push(
                    <path
                      key={`fim-${sm.processStepId}`}
                      className="flowcv__edge flowcv__edge--sym"
                      d={`M ${a.x} ${a.y} C ${a.x + d.x * c} ${a.y + d.y * c}, ${px} ${py}, ${px} ${py}`}
                      markerEnd="url(#flow-seta)"
                    />,
                  )
                }
                return linhas
              })}
              {religando
                ? (() => {
                    // com alvo, a previa e a curva final encaixada na porta;
                    // sem alvo: arrastando a PONTA a seta segue o ponteiro,
                    // arrastando a ORIGEM ela continua apontando ao destino fixo
                    const o = religando.original
                    const fixo = porId.get(religando.ponta === 'to' ? o.from : o.to)
                    const alvoNo = religando.alvo ? porId.get(religando.alvo.id) : undefined
                    let d: string
                    if (fixo && alvoNo && religando.alvo) {
                      d =
                        religando.ponta === 'to'
                          ? caminho(fixo, alvoNo, o.fromSide, religando.alvo.lado)
                          : caminho(alvoNo, fixo, religando.alvo.lado, o.toSide)
                    } else {
                      d =
                        religando.ponta === 'to'
                          ? `M ${religando.sx} ${religando.sy} L ${religando.x} ${religando.y}`
                          : `M ${religando.x} ${religando.y} L ${religando.sx} ${religando.sy}`
                    }
                    return (
                      <path
                        className={`flowcv__edge flowcv__edge--tmp${religando.alvo ? ' flowcv__edge--tmp-alvo' : ''}`}
                        d={d}
                        markerEnd="url(#flow-seta)"
                      />
                    )
                  })()
                : null}
            </svg>

            {nos.map((n) => (
              <div
                key={n.processStepId}
                data-no={n.processStepId}
                className={`flownode${arrastando === n.processStepId ? ' flownode--dragging' : ''}${
                  noSelecionado === n.processStepId ? ' flownode--on' : ''
                }${etapaDestacada === n.processStepId ? ' flownode--item' : ''}${
                  alvoAtual?.id === n.processStepId ? ' flownode--alvo' : ''
                }`}
                // selo sobre a etapa destacada: "WI-xxxx está nesta etapa"
                data-item={etapaDestacada === n.processStepId ? (destacarItemCodigo ?? 'O item') : undefined}
                style={{ left: n.x, top: n.y, width: larguraDe(n), borderLeftColor: n.color ?? 'var(--ob-primary)' }}
                onPointerDown={(e) => {
                  // visualizacao: caixa inerte (o pan do fundo assume)
                  if (!modoEdicao) return
                  const el = e.target as HTMLElement
                  if (el.closest('button') || el.closest('.flownode__port') || el.closest('.flownode__nameinput')) return
                  setNoSelecionado(n.processStepId)
                  setArestaSelecionada(null)
                  const p = pontoNoCanvas(e)
                  dragRef.current = {
                    id: n.processStepId,
                    dx: p.x - n.x,
                    dy: p.y - n.y,
                    startX: p.x,
                    startY: p.y,
                    moveu: false,
                    // clique parado no nome (sem arrastar) abre o renomear
                    peloNome: Boolean(el.closest('.flownode__name')),
                  }
                  setArrastando(n.processStepId)
                }}
              >
                <div className="flownode__head">
                  <i
                    className={`${n.icon ?? 'fas fa-circle-notch'} flownode__ico`}
                    aria-hidden
                    style={{ color: n.color ?? 'var(--ob-primary)' }}
                  />
                  {renomeando?.id === n.processStepId ? (
                    <input
                      className="flownode__nameinput"
                      autoFocus
                      value={renomeando.valor}
                      onPointerDown={(e) => e.stopPropagation()}
                      onChange={(e) => setRenomeando({ id: n.processStepId, valor: e.target.value })}
                      onBlur={confirmarRenomeio}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') confirmarRenomeio()
                        if (e.key === 'Escape') {
                          e.stopPropagation()
                          setRenomeando(null)
                        }
                      }}
                    />
                  ) : (
                    <span
                      className="flownode__name"
                      title="Clique para renomear a etapa (vale no cadastro inteiro); arraste para mover"
                    >
                      {n.name}
                    </span>
                  )}
                  {modoEdicao ? (
                    <button
                      type="button"
                      className="flownode__gear"
                      aria-label={`Configurar ${n.name}`}
                      title="Configurar a etapa (ícone)"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={() => setIconeDe(n.processStepId)}
                    >
                      <i className="fas fa-cog" aria-hidden />
                    </button>
                  ) : null}
                  {modoEdicao ? (
                    <button
                      type="button"
                      className="flownode__remove"
                      aria-label={`Remover ${n.name} do fluxo`}
                      title="Remover do fluxo — se este for o único fluxo dela, a etapa sai do cadastro ao salvar"
                      onClick={() => removerNo(n.processStepId)}
                    >
                      <i className="far fa-trash-alt" aria-hidden />
                    </button>
                  ) : null}
                </div>
                {/* portas so na EDICAO: na visualizacao elas desaparecem */}
                {modoEdicao
                  ? (['top', 'right', 'bottom', 'left'] as const).map((lado) => (
                      <span
                        key={lado}
                        className={`flownode__port flownode__port--${lado}${
                          alvoAtual?.id === n.processStepId && alvoAtual.lado === lado
                            ? ` flownode__port--alvo${alvoAtual.exato ? ' flownode__port--alvo-exato' : ''}`
                            : ''
                        }`}
                        title={`Ligar ${n.name} a outra etapa (arraste; solte no vazio para criar uma nova)`}
                        onPointerDown={(e) => {
                          e.stopPropagation()
                          const p = pontoNoCanvas(e)
                          // a linha nasce no CENTRO da porta, nao onde o dedo pegou
                          const a = ancora(n, lado)
                          setLigando({ from: n.processStepId, fromSide: lado, x: p.x, y: p.y, sx: a.x, sy: a.y, alvo: null })
                        }}
                      />
                    ))
                  : null}
              </div>
            ))}

            {/* SIMBOLOS de inicio/fim de fluxo (2026-09-02): circulo de play
                antes da etapa que pode comecar o fluxo; bandeira depois da
                etapa final. Clique (na edicao) remove com confirmacao. */}
            {(grafo.data?.steps ?? []).flatMap((sm) => {
              const no = porId.get(sm.processStepId)
              if (!no) return []
              const flags = sm as {
                id: string
                isInitial?: boolean
                isFinal?: boolean
                startPositionX?: number | null
                startPositionY?: number | null
                finalPositionX?: number | null
                finalPositionY?: number | null
              }
              const feitos = [] as React.ReactNode[]
              if (flags.isInitial) {
                const px = flags.startPositionX ?? no.x - 110
                const py = flags.startPositionY ?? no.y + ALTURA_NO / 2
                feitos.push(
                  <button
                    key={`sym-ini-${sm.processStepId}`}
                    type="button"
                    className="flowsym flowsym--inicio"
                    style={{ left: px - 16, top: py - 16 }}
                    title={
                      modoEdicao
                        ? `Início de fluxo — itens podem começar por “${no.name}”. Clique para remover.`
                        : `Início de fluxo — itens podem começar por “${no.name}”.`
                    }
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => {
                      if (!modoEdicao) return
                      confirm.ask(`Remover o início de fluxo de “${no.name}”? Itens deixarão de poder começar por esta etapa.`, () =>
                        salvarConfigEtapa.mutate({ flowStepId: flags.id, isInitial: false, startX: null, startY: null }),
                      )
                    }}
                  >
                    <i className="fas fa-play" aria-hidden />
                  </button>,
                )
              }
              if (flags.isFinal) {
                const px = flags.finalPositionX ?? no.x + larguraDe(no) + 110
                const py = flags.finalPositionY ?? no.y + ALTURA_NO / 2
                feitos.push(
                  <button
                    key={`sym-fim-${sm.processStepId}`}
                    type="button"
                    className="flowsym flowsym--fim"
                    style={{ left: px - 16, top: py - 16 }}
                    title={
                      modoEdicao
                        ? `Fim de fluxo em “${no.name}”. Clique para remover.`
                        : `Fim de fluxo em “${no.name}”.`
                    }
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => {
                      if (!modoEdicao) return
                      confirm.ask(`Remover o fim de fluxo de “${no.name}”?`, () =>
                        salvarConfigEtapa.mutate({ flowStepId: flags.id, isFinal: false, finalX: null, finalY: null }),
                      )
                    }}
                  >
                    <i className="fas fa-flag-checkered" aria-hidden />
                  </button>,
                )
              }
              return feitos
            })}

            {/* Camada das ALCAS acima dos cartoes: a ponta da seta fica
                agarravel mesmo encostada na caixa (antes o cartao e a porta
                engoliam o clique). Arraste para reconectar no lado desejado. */}
            <svg
              className={`flowcv__tips${pontaNaMao ? ' flowcv__tips--inerte' : ''}`}
              aria-hidden
              style={modoEdicao ? undefined : { display: 'none' }}
            >
              {arestas.map((a) => {
                const de = porId.get(a.from)
                const para = porId.get(a.to)
                if (!de || !para) return null
                const on = arestaSelecionada?.from === a.from && arestaSelecionada?.to === a.to
                const ponta = pontaFinal(de, para, a.fromSide, a.toSide)
                const origem = pontaInicial(de, para, a.fromSide)
                // pega uma EXTREMIDADE: a outra fica fixa e a solta reconecta
                // em outra face da mesma etapa ou em outra etapa
                const pegar = (qual: 'from' | 'to') => (e: React.PointerEvent) => {
                  e.stopPropagation()
                  const p = pontoNoCanvas(e)
                  const fixa =
                    qual === 'to'
                      ? ancora(de, a.fromSide ?? ladosAuto(de, para)[0])
                      : ancora(para, a.toSide ?? ladosAuto(de, para)[1])
                  // a ligacao sai do desenho enquanto e arrastada
                  setArestas((atual) => atual.filter((x) => !(x.from === a.from && x.to === a.to)))
                  setArestaSelecionada(null)
                  setReligando({ original: a, ponta: qual, x: p.x, y: p.y, sx: fixa.x, sy: fixa.y, alvo: null })
                }
                return (
                  <g key={`tip-${a.from}>${a.to}`}>
                    <circle className={`flowcv__tip${on ? ' flowcv__tip--on' : ''}`} cx={ponta.x} cy={ponta.y} r={7} />
                    <circle
                      className={`flowcv__tip flowcv__tip--origem${on ? ' flowcv__tip--on' : ''}`}
                      cx={origem.x}
                      cy={origem.y}
                      r={6}
                    />
                    {/* areas de pegada maiores e invisiveis */}
                    <circle className="flowcv__tiphit" cx={ponta.x} cy={ponta.y} r={13} onPointerDown={pegar('to')}>
                      <title>Arraste a ponta da seta para reconectar no lado que quiser</title>
                    </circle>
                    <circle className="flowcv__tiphit" cx={origem.x} cy={origem.y} r={13} onPointerDown={pegar('from')}>
                      <title>Arraste a origem da ligação para sair de outro lado ou de outra etapa</title>
                    </circle>
                    {/* ALCA DE CURVATURA (SPEC-20261103-2312): so na ligacao
                        selecionada; arraste muda a barriga da curva, duplo
                        clique volta ao automatico */}
                    {on
                      ? (() => {
                          const meio = pontoMedio(de, para, a.fromSide, a.toSide, a.bend)
                          const segurando = curvando?.from === a.from && curvando?.to === a.to
                          return (
                            <>
                              <circle
                                className={`flowcv__bend${segurando ? ' flowcv__bend--on' : ''}${a.bend ? ' flowcv__bend--custom' : ''}`}
                                cx={meio.x}
                                cy={meio.y}
                                r={6}
                              />
                              <circle
                                className="flowcv__bendhit"
                                cx={meio.x}
                                cy={meio.y}
                                r={14}
                                onPointerDown={(e) => {
                                  e.stopPropagation()
                                  setCurvando({ from: a.from, to: a.to })
                                }}
                                onDoubleClick={(e) => {
                                  e.stopPropagation()
                                  setArestas((atual) => atual.map((x) => (x.from === a.from && x.to === a.to ? { ...x, bend: null } : x)))
                                }}
                              >
                                <title>Arraste para mudar a curvatura da ligação; duplo clique volta à curva automática</title>
                              </circle>
                            </>
                          )
                        })()
                      : null}
                  </g>
                )
              })}
            </svg>
          </div>
          </div>
        </div>
        </div>

        </div>

        <p className="adm-hint">
          {nos.length === 0
            ? 'Fluxo vazio — comece adicionando uma etapa.'
            : `${nos.length} ${nos.length === 1 ? 'etapa' : 'etapas'} · ${arestas.length} ${arestas.length === 1 ? 'ligação' : 'ligações'}. As lanes dos quadros apontam para as etapas deste desenho.`}
        </p>
      </FormSection>
      {/* Soltar a linha do vazio num cartão: o ponto vira INÍCIO ou FIM? */}
      {perguntaSimbolo ? (
        <Modal
          open
          onClose={() => setPerguntaSimbolo(null)}
          title="O que este ponto marca?"
          width={460}
          footer={
            <Button variant="secondary" onClick={() => setPerguntaSimbolo(null)}>
              Cancelar
            </Button>
          }
        >
          {(() => {
            const salva = grafo.data?.steps.find((x) => x.processStepId === perguntaSimbolo.processStepId)
            const nome = porId.get(perguntaSimbolo.processStepId)?.name ?? 'a etapa'
            if (!salva) {
              return (
                <p className="flowcfg__aviso">
                  Salve o desenho primeiro — a etapa recém-desenhada ainda não existe no cadastro.
                </p>
              )
            }
            const escolher = (tipo: 'inicio' | 'fim') => {
              salvarConfigEtapa.mutate(
                tipo === 'inicio'
                  ? { flowStepId: salva.id, isInitial: true, startX: perguntaSimbolo.x, startY: perguntaSimbolo.y }
                  : { flowStepId: salva.id, isFinal: true, finalX: perguntaSimbolo.x, finalY: perguntaSimbolo.y },
              )
              setPerguntaSimbolo(null)
            }
            return (
              <div className="flowsym__escolha">
                <button type="button" className="flowsym__opcao" onClick={() => escolher('inicio')}>
                  <span className="flowsym flowsym--inicio flowsym--amostra" aria-hidden>
                    <i className="fas fa-play" />
                  </span>
                  <span className="flowsym__optexto">
                    <strong>Início de fluxo</strong>
                    <span>Itens de Trabalho podem COMEÇAR por “{nome}” — na criação e no quadro.</span>
                  </span>
                </button>
                <button type="button" className="flowsym__opcao" onClick={() => escolher('fim')}>
                  <span className="flowsym flowsym--fim flowsym--amostra" aria-hidden>
                    <i className="fas fa-flag-checkered" />
                  </span>
                  <span className="flowsym__optexto">
                    <strong>Fim de fluxo</strong>
                    <span>“{nome}” encerra o fluxo — o símbolo marca a saída no desenho.</span>
                  </span>
                </button>
              </div>
            )
          })()}
        </Modal>
      ) : null}
      {/* CONFIGURAÇÕES da etapa, abertas pela engrenagem do cartão
          (2026-09-01): ícone + o status que o item assume ao entrar. */}
      {etapaDoIcone ? (
        <Modal
          open
          onClose={() => setIconeDe(null)}
          title={`Configurações — "${etapaDoIcone.name}"`}
          width={640}
          footer={
            <Button variant="secondary" onClick={() => setIconeDe(null)}>
              Fechar
            </Button>
          }
        >
          <div className="ob-stack">
            <FieldBlock
              label="Status ao entrar na etapa"
              hint="Quando um Item de Trabalho entrar nesta etapa, ele muda para o status escolhido. O padrão é não mudar."
            >
              {() =>
                etapaSalva ? (
                  <Select
                    value={etapaSalva.onEnterStatusId ?? ''}
                    placeholder="Não muda o status"
                    options={[
                      { value: '', label: 'Não muda o status' },
                      ...(bootstrap.data?.workItemStatuses ?? [])
                        .filter((st) => st.isActive)
                        .map((st) => ({ value: st.id, label: t(st.nameTranslationKey) })),
                    ]}
                    onChange={(v) =>
                      salvarConfigEtapa.mutate({ flowStepId: etapaSalva.id, onEnterStatusId: v || null })
                    }
                  />
                ) : (
                  <p className="flowcfg__aviso">Salve o fluxo primeiro — a etapa recém-desenhada ainda não existe no cadastro.</p>
                )
              }
            </FieldBlock>
            <FieldBlock
              label="Início e fim de fluxo"
              hint="Início: um item pode começar o fluxo por esta etapa. Fim: a etapa encerra o fluxo. Os símbolos aparecem no desenho."
            >
              {() =>
                etapaSalva ? (
                  <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                    <Checkbox
                      label="Um item pode iniciar por esta etapa"
                      checked={Boolean((etapaSalva as { isInitial?: boolean }).isInitial)}
                      onChange={(e) =>
                        salvarConfigEtapa.mutate({ flowStepId: etapaSalva.id, isInitial: e.target.checked })
                      }
                    />
                    <Checkbox
                      label="Etapa final do fluxo"
                      checked={Boolean((etapaSalva as { isFinal?: boolean }).isFinal)}
                      onChange={(e) =>
                        salvarConfigEtapa.mutate({ flowStepId: etapaSalva.id, isFinal: e.target.checked })
                      }
                    />
                  </div>
                ) : (
                  <p className="flowcfg__aviso">Salve o fluxo primeiro — a etapa recém-desenhada ainda não existe no cadastro.</p>
                )
              }
            </FieldBlock>
            {/* botão + prévia (pedido 2026-09-01): o seletor abre por cima,
                e ao lado do botão fica só o ícone escolhido */}
            <IconField
              label="Ícone"
              value={etapaDoIcone.icon ?? 'fas fa-circle-notch'}
              onChange={(escolha) =>
                trocarIcone.mutate({
                  id: etapaDoIcone.processStepId,
                  icon: escolha.iconImageId ? `/public/catalog-icon/${escolha.iconImageId}` : escolha.icon,
                })
              }
            />
          </div>
        </Modal>
      ) : null}
      {confirm.element}
    </FormScreen>
  )
}
